import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAnalytics from "../../hooks/use-analytics";
import Modal, { useShowModal } from "../../hooks/use-modal";
import usePathParams from "../../hooks/use-path-params";
import useClusterDetail from "../../hooks/use-cluster-detail";
import useSendRequest from "../../hooks/use-send-request";
import NotFound from "../NotFound";
import clxs from "./property-cluster-detail.module.css";
import PropertyClusterLayout from "./PropertyClusterLayout";
import ClusterPriceCalculatorContext from "../../hooks/use-cluster-price-calculator/ClusterPriceCalculatorContext";
import { useQueryParamValue, useSetQueryParam } from "../../hooks/use-query-param";
import useReservationService from "../../hooks/use-reservation-service";
import { useSnackbar } from "../../hooks/use-snackbar";
import { useSession, useUser } from "../../hooks/use-session";
import SearchHeader from "../PropertyListing/SerachHeader";
import { useScroll } from "../../hooks/use-scroll";
import useIsMobile from "../../hooks/use-is-mobile";
import useClx from "../../hooks/use-clx";
import ClusterGalleryModal from "./ClusterBanner/ClusterGalleryModal";
import { useClusterCoupons } from "../../hooks/use-cluster-coupons";
import useUtm from "../../hooks/use-utm";
import prepareMeta from "../../../utils/prepare-meta";

function PropertClusterDetail () {
  const { pathname, search } = useLocation(),
    push = useNavigate(),
    { scroll } = useScroll(),
    { isMobile } = useIsMobile(),
    showGalleryModal = useShowModal(GALLERY_MODAL_ID),
    { utm } = useUtm(),
    { showSendRequestModal } = useSendRequest(),
    priceCalculatorDesktopRef = useRef<HTMLDivElement>(null),
    session = useSession(),
    { service: reservationService } = useReservationService(),
    // LOHONO_BRAND = 1,
    enqueueSnackbar = useSnackbar(),
    { track } = useAnalytics(),
    user = useUser(),
    destination = usePathParams(PATH, "destination"),
    location = usePathParams(PATH, "location"),
    cluster_slug = usePathParams(PATH, "cluster_slug"),
    checkin_date = useQueryParamValue("date", CHECKIN_DATE_KEY),
    checkout_date = useQueryParamValue("date", CHECKOUT_DATE_KEY),
    adultCount = useQueryParamValue("integer", ADULT_COUNT_KEY, 2),
    childCount = useQueryParamValue("integer", CHILD_COUNT_KEY),
    offering_type_ids = useQueryParamValue("string", OFFERING_ID_KEY),
    coupon_code = useQueryParamValue("string", COUPON_CODE_KEY),
    setCouponCode = useSetQueryParam("string", COUPON_CODE_KEY),
    [loader, setLoader] = useState<boolean>(false),
    [isPriceInHeaderVisible, setIsPriceInHeaderVisible] = useState<boolean>(false),
    location_slug = useMemo(
      () => `${destination}-${location}`,
      [destination, location],
    ),
    { clusterDetail, isInitialDataLoaded} = useClusterDetail(cluster_slug, adultCount, checkin_date, checkout_date,offering_type_ids),
    { coupons } = useClusterCoupons(
      cluster_slug,
      checkin_date?.toISOString(),
      checkout_date?.toISOString(),
      adultCount,
      childCount,
      offering_type_ids,
    ),
    isShowPriceCalculator = useMemo(
      () => {
        if (!isMobile) {
          return true;
        }
        return false;
      }, [isMobile],
    ),
    {upcoming} = useMemo(
      () => ({upcoming: clusterDetail?.upcoming}),
      [clusterDetail],
    ),
    handleShowGallery = (selectedPropertyImages: ClusterPropertyImages) => showGalleryModal({ clusterVillas: clusterDetail?.images ?? [],villaSelected:selectedPropertyImages }),
    handleShare = async () => {
      if (!clusterDetail) {
        return true;
      }

      const url = window.location.href, share = {
        title: clusterDetail.name,
        text: clusterDetail.description || "Luxury Lohono Villa",
        url: url,
      };

      try {
        await window.navigator.share(share);
      } catch (err) {
        await window.navigator.clipboard.writeText(url);
        return false;
      }

      return true;
    },
    handleSendRequest = (values: any = {}, clickedBy: string) => {
      return showSendRequestModal({
        interest: "rental",
        location: location_slug,
        meta: values,
        clickedBy: clickedBy,
      });
      
    },
  // If coupon code is not present, check for “discount_method” value if its one of gold, silver, platinum. if yes, then send coupon_code in the reqeust.  
  getCouponValueToset = (
    response_coupon_code?: string,
    discount_method?: string,
  ) => {
    let couponToSet = "";
    if (response_coupon_code) {
      couponToSet = response_coupon_code;
    } else if (discount_method && discount_method.length > 0) {
      couponToSet = (discount_method == "gold" || discount_method == "silver" || discount_method == "platinum") ? discount_method : "";
    } else {
      couponToSet = coupon_code || "";
    }
      // else {
      //   couponToSet = "NO_AVAIL"
      // }
    return couponToSet;
  },
  prepareSearchValues = (
    urlString: string,
    booking_id: string,
    booking_slugs: string[], 
    coupon_code?: string,
    discount_method?: string,
    tier?: string,
  ) => {
    // Check if "booking_id=" and "booking_slugs=" exist
    const bookingIdExists = urlString.includes("booking_id=");
    const bookingSlugsExists = urlString.includes("booking_slugs=");
    const couponCodeExists = urlString.includes("coupon_code=");
    const tierExists = urlString.includes("tier=");
    
    if (bookingIdExists) {
        const valueToReplace = booking_id ? ("&booking_id=" + booking_id) : "";
        // Remove "booking_id=" and its value
        urlString = urlString.replace(/&?booking_id=[^&]*/, valueToReplace);
    } else {
      urlString += "&booking_id=" + booking_id;
    }
    
    if (bookingSlugsExists) {
        const valueToReplace = booking_slugs ? ("&booking_slugs=" + booking_slugs.join("-")) : "";
        // Remove "booking_slugs=" and its value
        urlString = urlString.replace(/&?booking_slugs=[^&]*/, valueToReplace);
    } else {
      urlString += "&booking_slugs=" + booking_slugs.join("-");
    }


    if (tierExists) {
      const valueToReplace = tier ? ("&tier=" + tier) : "";
      // Remove "quote_id=" and its value
      urlString = urlString.replace(/&?tier=[^&]*/, valueToReplace);
    } else {
      urlString += "&tier=" + tier;
    }

    const couponToSet = getCouponValueToset(coupon_code, discount_method);
    setCouponCode(couponToSet);
    if (couponCodeExists) {
      const valueToReplace = (coupon_code || couponToSet) ? ("&coupon_code=" + couponToSet) : "";
      // Remove "coupon_code=" and its value
      urlString = urlString.replace(/&?coupon_code=[^&]*/, valueToReplace);
    } else {
      urlString += "&coupon_code=" + couponToSet;
    }
    
    // Clean up any leading '&' if necessary
    urlString = urlString.replace(/(\?&|&&)/g, "?").replace(/&$/, "");
    
    return urlString;
  },handleBook = async (values: any) => {
      track("property_detail_book_clicked");

      const {
        stay = { start: null, end: null },
        occupancy = { adult_count: 0, child_count: 0 },
        // pre_book_meals = false,
      } = values,
      { start: checkin_date, end: checkout_date } = stay,
      { adult_count, child_count } = occupancy;

      const meta = prepareMeta(utm);

      if(meta) {
        meta["isCluster"] = true;
      }

      // Send tier level in coupon code if no coupon code is used.
      // const tierLevel = user?.loyalty_point_metadata?.tier;
      // let isAddTierToCoupon: boolean = false;
      // if(tierLevel?.length && (!coupon_code || coupon_code === "NONE")) {
      //   isAddTierToCoupon = true;
      // }

      const { error: e1, response: r1 } = await reservationService.createClusterBooking(
        {
          cluster_slug: cluster_slug,
          checkin_date: checkin_date.toISOString(),
          checkout_date: checkout_date.toISOString(),
          adult_count: adult_count,
          child_count: child_count,
          coupon_code: coupon_code || "",
          offering_type_ids: offering_type_ids,
          meta: meta,
        },
        session,
      );

    if (e1) {
      enqueueSnackbar(e1.message, "error");
      return;
    }

    if (!r1) {
      enqueueSnackbar("unknown error", "error");
      return;
    }
      const { booking_id, booking_slugs, response_coupon_code, discount_method } = r1;

      if (!booking_id) {
        enqueueSnackbar("Booking cannot be made due to some reason. Please contact our customer service.", "error");
      }

      const checkoutUrl = `${pathname}/checkout`;

      const tier = user?.loyalty_point_metadata?.tier;
      
      //Remove old booking ids and slugs, quote id and protect flag in case user comes back from the checkout page. 
      const checkoutSearchParam = prepareSearchValues(search,booking_id, booking_slugs, response_coupon_code, discount_method, tier);

      return push({
        pathname: checkoutUrl,
        search: checkoutSearchParam,
      });
    },
    /**
     * Determine the vertical offset to scroll to based on coupon availability. If there is 0 or 1 coupon, scroll to a default offset (680). Otherwise, scroll to the position of the exclusive coupon offers section.Use 1000 as a fallback if the ref is unavailable.
     */
    // offSetTop = coupons.length <= 1 ? 680 : exclusiveCouponOffersRef?.current?.offsetTop ?? 1000,
    showPriceCalculatorInHeader = useMemo(() => {
      if(isMobile) {
        return (scroll >= 50); 
      } else {
        if(coupons.length > 1) {
          // return (scroll >= offSetCouponTopCached);
          return isPriceInHeaderVisible;
        } else {
          return false;
        }
      }
}, [scroll, isMobile, isPriceInHeaderVisible]),
    hcx = useClx(
      showPriceCalculatorInHeader ?   clxs.searchVisible :  clxs.searchInVisible,
    ),
    sscx = useClx(
      showPriceCalculatorInHeader ? clxs.searchDisplayNon : clxs.searchVisible,
    ),
    phcx = useClx(
      showPriceCalculatorInHeader ?   `${clxs.priceCalculator} ${clxs.searchInVisible}` : clxs.searchVisible ,
    );

  useEffect(() => {
    if (!priceCalculatorDesktopRef.current) return;
  
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const { bottom } = entry.boundingClientRect;
          const viewportHeight = window.innerHeight;

          const bottomVisible = bottom > 0 && bottom <= viewportHeight;
          // const topVisible = top >= 0 && top <= viewportHeight;

          // Show price in header only when price calculator is not visible on the screen.
          if (!entry.isIntersecting && bottomVisible) {
            setIsPriceInHeaderVisible(true);  // show component
          } else {
            setIsPriceInHeaderVisible(false); // hide component
          }
        });
      },
      {
        root: null, // viewport
        threshold: 0.3, 
      },
    );
  
    observer.observe(priceCalculatorDesktopRef.current);
  
    return () => {
      if (priceCalculatorDesktopRef.current) {
        observer.unobserve(priceCalculatorDesktopRef.current);
      }
    };
  }, [isShowPriceCalculator, priceCalculatorDesktopRef,upcoming]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => { 
    if(isInitialDataLoaded){
      setLoader(false);
    }else{
      setLoader(!isInitialDataLoaded);
    }
  }, [isInitialDataLoaded]);
    
  if (!clusterDetail && isInitialDataLoaded) {
    return <NotFound />;
  }

  if (!clusterDetail) {
    return <div style={{height: "100vh"}}></div>;
  }

  return (
   <>
    <ClusterPriceCalculatorContext
      path={PATH}
      id={CLUSTER_PRICE_CALCULATOR_ID}
      clusterSlugKey={CLUSTER_SLUG_KEY}
      checkinDateKey={CHECKIN_DATE_KEY}
      checkoutDateKey={CHECKOUT_DATE_KEY}
      adultCountKey={ADULT_COUNT_KEY}
      childCountKey={CHILD_COUNT_KEY}
      couponCodeKey={COUPON_CODE_KEY}
      offeringTypeIdsKey={OFFERING_ID_KEY}
    >
      <>
        <SearchHeader
          path={PATH}
          scrollShowClassName = {hcx}
          scrollHideClassName={sscx}
          className={showPriceCalculatorInHeader ? clxs.search : clxs.hideSearch}
          onShare={handleShare}
          isShowShareIcon={true}
          detailPageSearchClassname = {clxs.searchBar}
          checkinDateKey={CHECKIN_DATE_KEY}
          checkoutDateKey={CHECKOUT_DATE_KEY}
          couponCodeKey={COUPON_CODE_KEY}
          id={CLUSTER_PRICE_CALCULATOR_ID}
          priceCalculatorHeaderClassname={phcx}
          isDetailPage={true}
          isClusterDetail={true}
          offeringTypeIdsKey={OFFERING_ID_KEY}
          clusterDetails={clusterDetail}
        />
          <PropertyClusterLayout
            isInitialDetailsLoading={loader}
            ref={priceCalculatorDesktopRef}
            priceCalculatorId={CLUSTER_PRICE_CALCULATOR_ID}
            path={PATH}
            locationSlug={location_slug}
            clusterDetails={clusterDetail}
            onShowGallery={handleShowGallery}
            onShare={handleShare}
            onSendRequest= {(values: any) => handleSendRequest(values,"Need Help")}
            onBook={handleBook}
            clusterSlugKey={CLUSTER_SLUG_KEY}
            checkinDateKey={CHECKIN_DATE_KEY}
            checkoutDateKey={CHECKOUT_DATE_KEY}
            adultCountKey={ADULT_COUNT_KEY}
            childCountKey={CHILD_COUNT_KEY}
            couponCodeKey={COUPON_CODE_KEY}
            availLoyaltyPointsKey={AVAIL_LOYALTY_POINTS_KEY}
            seniorCitizenDescription={clusterDetail.senior_citizen.description}
            seniorCitizenFriendly={clusterDetail.senior_citizen.senior_citizen_friendly}
            isShowPriceCalculator={isShowPriceCalculator}
            coupons={coupons}
          />
        <Modal id={GALLERY_MODAL_ID}>
          <ClusterGalleryModal />
        </Modal>
      </>
    </ClusterPriceCalculatorContext> 
   </>
  );
}

export default PropertClusterDetail;

const GALLERY_MODAL_ID = "cluster-gallery-mobile";

const PATH = "/cluster-villas/:destination/:location/:cluster_slug";

const CLUSTER_SLUG_KEY = "cluster_slug";

const CHECKIN_DATE_KEY = "checkin_date";

const CHECKOUT_DATE_KEY = "checkout_date";

const ADULT_COUNT_KEY = "adult_count";

const CHILD_COUNT_KEY = "child_count";

const COUPON_CODE_KEY = "coupon_code";

const AVAIL_LOYALTY_POINTS_KEY = "avail_loyalty_points";

const CLUSTER_PRICE_CALCULATOR_ID = "cluster-details-price-calculator";

const OFFERING_ID_KEY = "offering_type_ids";